import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, extractError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Check, Loader2, Sparkles, HeartHandshake } from "lucide-react";

const FAMILY_TIERS = [
  {
    key: "family_free", name: "Free Explorer", monthly: 0, yearly: 0, cta: null,
    features: [
      "Create family & child profiles",
      "Browse specialized babysitters",
      "View up to 5 sitter profiles",
      "Save up to 2 favorites",
      "Receive booking requests",
      "Basic messaging (first 3 free, then $2 each — upgrade for unlimited)",
    ],
  },
  {
    key: "family_plus", name: "Family Plus", monthly: 14.99, yearly: 20, popular: true,
    features: [
      "Everything in Free",
      "Unlimited messaging",
      "Unlimited favorites",
      "AI compatibility matching",
      "Advanced provider filters",
      "Priority customer support",
      "Care journal",
      "Digital care binder",
      "Family calendar",
      "Session summaries",
    ],
  },
  {
    key: "family_premium", name: "Family Premium", monthly: 29.99, yearly: 24,
    features: [
      "Everything in Family Plus",
      "Priority booking requests",
      "Last-minute booking access",
      "Travel planning support",
      "Emergency booking assistance",
      "Behavioral insights dashboard",
      "Dedicated account support",
    ],
  },
];

const SITTER_TIERS = [
  {
    key: "sitter_free", name: "Free", monthly: 0, yearly: 0,
    features: [
      "Visible to families within 10 miles",
      "Standard search placement",
      "Not eligible for last-minute bookings",
      "Cannot read family reviews",
    ],
  },
  {
    key: "sitter_enhanced", name: "Enhanced", monthly: 7.99, yearly: 12.99,
    features: [
      "Unlimited family reach — no distance cap",
      "Top of search results",
      "Read family reviews left about you",
      "Eligible for last-minute bookings",
    ],
  },
];

function PlanCard({ tier, currentPlan, onSelect, roleAllowed, period }) {
  const isCurrent = currentPlan?.plan === tier.key;
  const price = period === "year" ? tier.yearly : tier.monthly;
  return (
    <div className={`paper-card p-6 flex flex-col relative transition-all duration-200 ${
      tier.popular ? "border-sage-500 shadow-md" : ""
    } ${isCurrent ? "ring-2 ring-sage-500" : ""}`}
      data-testid={`plan-card-${tier.key}`}>
      {tier.popular && (
        <div className="absolute -top-3 left-6 chip-ochre"><Sparkles className="w-3 h-3 mr-1" /> Most popular</div>
      )}
      <h3 className="font-heading text-2xl font-bold text-sage-900">{tier.name}</h3>
      <div className="mt-2 flex items-baseline gap-1">
        <span className="text-4xl font-heading font-bold text-sage-900">
          {price === 0 ? "Free" : `$${price}`}
        </span>
        {price > 0 && <span className="text-sm text-stone2-500">/mo{period === "year" ? " (yearly)" : ""}</span>}
      </div>
      <ul className="mt-6 space-y-2 flex-1">
        {tier.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-sage-900">
            <Check className="w-4 h-4 text-sage-600 mt-0.5" /> {f}
          </li>
        ))}
      </ul>
      {isCurrent ? (
        <div className="mt-6 text-center text-sm font-semibold text-sage-700 border border-sage-200 bg-sage-50 rounded-lg py-2">Current plan</div>
      ) : price === 0 || !roleAllowed ? (
        <div className="mt-6 text-center text-xs text-stone2-500">
          {roleAllowed ? "Included by default" : "Not available for your account"}
        </div>
      ) : (
        <button type="button" onClick={() => onSelect(tier.key, period)}
          data-testid={`plan-select-${tier.key}-${period}`}
          className="mt-6 btn-primary w-full justify-center">
          Upgrade
        </button>
      )}
    </div>
  );
}

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState(null);
  const [period, setPeriod] = useState("month");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/subscriptions/me").then((r) => setCurrent(r.data)).catch(() => {});
  }, [user]);

  const onSelect = async (plan, chosenPeriod) => {
    if (!user) { navigate("/register"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/subscriptions/checkout", {
        plan, period: chosenPeriod, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(extractError(e)); setBusy(false); }
  };

  const role = user?.role;

  return (
    <div className="min-h-screen bg-stone2-50" data-testid="pricing-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-14">
        <div className="text-center">
          <span className="section-eyebrow">Membership</span>
          <h1 className="mt-2 text-3xl lg:text-5xl font-heading font-bold text-sage-900 flex items-center justify-center gap-2">
            <HeartHandshake className="w-8 h-8 text-sage-600" /> Choose your plan
          </h1>
          <p className="text-stone2-600 mt-3 max-w-xl mx-auto">
            Simple plans for families and sitters. Upgrade any time, cancel any time.
          </p>
          <div className="mt-6 inline-flex items-center rounded-full bg-white border border-stone2-200 p-1">
            <button type="button" onClick={() => setPeriod("month")}
              data-testid="pricing-period-monthly"
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors duration-200 ${
                period === "month" ? "bg-sage-600 text-white" : "text-sage-800"
              }`}>Monthly</button>
            <button type="button" onClick={() => setPeriod("year")}
              data-testid="pricing-period-yearly"
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors duration-200 ${
                period === "year" ? "bg-sage-600 text-white" : "text-sage-800"
              }`}>Yearly</button>
          </div>
        </div>

        <section className="mt-14">
          <h2 className="text-xl font-heading font-bold text-sage-900 mb-6">For Families</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {FAMILY_TIERS.map((t) => (
              <PlanCard key={t.key} tier={t} currentPlan={current}
                onSelect={onSelect} period={period}
                roleAllowed={!user || role === "family"} />
            ))}
          </div>
        </section>

        <section className="mt-16">
          <h2 className="text-xl font-heading font-bold text-sage-900 mb-6">For Specialized Babysitters</h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl">
            {SITTER_TIERS.map((t) => (
              <PlanCard key={t.key} tier={t} currentPlan={current}
                onSelect={onSelect} period={period}
                roleAllowed={!user || role === "sitter"} />
            ))}
          </div>
        </section>

        {busy && <div className="mt-6 text-center text-stone2-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Redirecting to Stripe…</div>}
      </div>
    </div>
  );
}
